import { computed, readonly, ref } from 'vue'
import { api } from 'boot/axios'
import { Role } from 'src/modules/core/helpers/role'
import logger from 'src/services/logger'
import dayjs from 'dayjs'
import { empty } from 'src/utils/variable.js'
import { auth } from 'src/services/auth'
import { wait } from 'src/utils/wait'

export const SessionState = {
  loading: 0,
  notLoggedIn: 1,
  loggedIn: 2,
  unknownAuthError: 3,
  cguConsentRequired: 4,
  personalDataUseConsentRequired: 5,
  fromErrorResponse(response) {
    if (response.status !== 401) {
      return SessionState.unknownAuthError
    }

    switch (response.data.error) {
      case 'insufficient_authentication':
        return SessionState.notLoggedIn
      case 'personal_data_use_not_approved':
        return SessionState.personalDataUseConsentRequired
      case 'cgu_not_accepted':
        return SessionState.cguConsentRequired
      default:
        return SessionState.unknownAuthError
    }
  }
}

export const sessionState = ref(null)

const user = ref(null)
const userReadOnly = readonly(user)

export const targetRoute = ref()
const loading = ref(true)
const urlSearchParams = new URLSearchParams(window.location.search)
const authError = ref(urlSearchParams.get('error'))
export const requiredIdentityProvider = ref(urlSearchParams.get('identityProvider'))
const authErrorReadOnly = readonly(authError)

export { userReadOnly as user, authErrorReadOnly as authError }

// Return true if at least one role match current user roles
export const isGranted = computed(() => {
  return roles => {
    if (!user.value || !Array.isArray(user.value.roles)) {
      return false
    }

    if (!Array.isArray(roles)) {
      roles = [roles]
    }

    const userRoles = user.value.roles.map(role => role.toLowerCase())
    for (const role of roles) {
      if (typeof role !== 'string') {
        throw new Error('Expected role to be a string. Got ' + typeof role)
      }

      if (userRoles.includes(role.toLowerCase())) {
        return true
      }
    }

    return false
  }
})
export const isAdmin = computed(() => {
  return isGranted.value(Role.Admin)
})
export const isProfessional = computed(() => {
  return isGranted.value(Role.Professional)
})
export const isPatient = computed(() => {
  return isGranted.value(Role.Patient)
})
export const isTenantAdmin = computed(() => {
  return user.value?.type === 'person' && !isAdmin.value
})

export const isConsentRequired = computed(() => {
  return sessionState.value === SessionState.cguConsentRequired ||
    sessionState.value === SessionState.personalDataUseConsentRequired
})

export async function checkSession(newTargetRoute = undefined, retry = false) {
  if (authError.value && !retry) { // Wait for user interaction before starting session
    throw new Error('Cannot call checkSession without clearing current error WITH MANUAL user action.')
  }

  if (sessionState.value === SessionState.loading) {
    logger.warn('Session already loading. Skipping session check.')
    return
  }

  if (sessionState.value === SessionState.loggedIn) {
    logger.warn('User already logged in. Skipping session check.')
    return
  }

  sessionState.value = SessionState.loading
  targetRoute.value = newTargetRoute
  authError.value = null

  try {
    user.value = (await api.get('/me', { fetchLocaleFromApi: true })).data

    // Current user is missing the required identity provider in the url. Redirect to the login page with the required identity provider.
    if (!empty(requiredIdentityProvider.value) &&
      (empty(user.value.identityProviders) || !user.value.identityProviders.includes(requiredIdentityProvider.value))) {
      auth.login(undefined, [requiredIdentityProvider.value])
      return
    }

    if (!empty(user.value.selectedTimezone)) {
      dayjs.tz.setDefault(user.value.selectedTimezone)
    }

    sessionState.value = SessionState.loggedIn
  } catch (error) {
    const { response } = error
    sessionState.value = SessionState.fromErrorResponse(response)
    if (sessionState.value === SessionState.unknownAuthError) {
      throw error
    }
  } finally {
    loading.value = false
  }
}

api.interceptors.response.use(response => {
  return response
}, async error => {
  const responseData = error.response?.data
  if (responseData?.error === 'REQUIRED_IDENTITY_PROVIDERS') {
    sessionState.value = SessionState.loading
    auth.login(undefined, responseData.data.identityProviders)
    await wait(3000)
  }

  return Promise.reject(error)
})

export const session = {
  sessionState,
  user: readonly(user),
  authError,
  loading,
  targetRoute,
  isAdmin,
  isPatient,
  isProfessional,
  isTenantAdmin,
  isGranted,
  checkSession
}
